import React from 'react';
import { Link } from 'react-router-dom';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useVirtualTourListingQuery } from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { EligibilityBadges, OpenInMatterport, StatusBadge } from '../VirtualTours/shared';
import { LinkDialog } from '../VirtualTours/TourActionDialogs';

/** Virtual tours linked to this listing, its plan allowance, and a Link action. Hidden without admin.virtual_tour.view. */
export const VirtualToursSection: React.FC<{ eid: number }> = ({ eid }) => {
  const canView = useGate(PERMISSIONS.virtualTour.view).allowed;
  const { data, isLoading, isError, error } = useVirtualTourListingQuery(eid, canView);
  const [linkOpen, setLinkOpen] = React.useState(false);

  if (!canView) return null;

  const allowance = data?.allowance;
  const allowanceText = allowance
    ? allowance.limit === -1
      ? `${allowance.used} virtual tour(s) used, unlimited`
      : `${allowance.used} of ${allowance.limit} virtual tours used`
    : null;

  return (
    <section className="mt-8 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Virtual tours</h3>
          {allowanceText && <p className="text-xs text-gray-500">{allowanceText}</p>}
        </div>
        <PermissionButton size="sm" variant="outline" code={PERMISSIONS.virtualTour.link} onClick={() => setLinkOpen(true)}>
          Link a Matterport model
        </PermissionButton>
      </div>

      {isError ? (
        <p className="text-sm text-red-600">{getApiErrorMessage(error, 'Could not load virtual tours.')}</p>
      ) : isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (data?.tours ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">No virtual tours linked.</p>
      ) : (
        <ul className="divide-y rounded-lg border border-gray-200 bg-white">
          {(data?.tours ?? []).map((t) => (
            <li key={t.vtid} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm">
              <StatusBadge status={t.status} />
              <EligibilityBadges planEligible={t.plan_eligible} source={t.source} />
              <span className="capitalize text-gray-600">{t.provider}</span>
              {t.external_id && <span className="font-mono text-xs text-gray-500">{t.external_id}</span>}
              <span className="ml-auto flex items-center gap-4">
                {t.provider === 'matterport' && <OpenInMatterport id={t.external_id} />}
                <Link to={`/admin/listings/virtual-tours?tab=tours&tour=${t.vtid}`} className="text-blue-600 hover:underline">
                  Manage
                </Link>
              </span>
            </li>
          ))}
        </ul>
      )}

      <LinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} eid={eid} />
    </section>
  );
};

export default VirtualToursSection;
